/**
 * Shared presentation helpers for the S2 Campaign Approvals screens.
 */
import type { TFunction } from 'i18next';
import type { BadgeProps } from '@teivor/shared-ui';
import { L } from '@teivor/shared-utils/i18n';

import type { CampaignApproval, CampaignApprovalStatus, ReapprovalReason } from '../types';

export interface ApprovalStatusBadge {
  variant: NonNullable<BadgeProps['variant']>;
  key: string;
  fallback: string;
}

export function statusBadge(status: CampaignApprovalStatus): ApprovalStatusBadge {
  switch (status) {
    case 'awaiting':
      return { variant: 'warning', key: 'com.approvals.status.awaiting', fallback: L('Čeká na schválení', 'Awaiting approval') };
    case 'confirmed':
      return { variant: 'success', key: 'com.approvals.status.confirmed', fallback: L('Schváleno', 'Approved') };
    case 'superseded_by_reapproval':
      return { variant: 'neutral', key: 'com.approvals.status.superseded', fallback: L('Nahrazeno', 'Superseded') };
  }
}

export function reapprovalReasonLabel(reason: ReapprovalReason, t: TFunction): string {
  switch (reason) {
    case 'icp_targeting':
      return t('com.approvals.reason.icp', 'Cílení (ICP) / Targeting (ICP)');
    case 'sequence_steps_templates':
      return t('com.approvals.reason.sequence', 'Kroky a šablony sekvence / Sequence steps & templates');
    case 'sending_identity':
      return t('com.approvals.reason.identity', 'Odesílací identita / Sending identity');
    case 'run_mode':
      return t('com.approvals.reason.run_mode', 'Režim běhu / Run mode');
  }
}

/** The API exposes campaignId only — render a stable, readable label + short ref. */
export function campaignTitle(approval: CampaignApproval, t: TFunction): string {
  const short = approval.campaignId.slice(0, 8);
  return `${t('com.approvals.campaign', 'Kampaň / Campaign')} ${short}`;
}
